// cSpell:ignore forfait Provisies unticked unticking Unticking unticks
// Which provision lines of a lease count in a settlement (Lewis, 2026-10-16):
// every line of the "Provisies" step has its own checkbox. Unticking one
// means only that provision does not count; the costs of its kind stay in
// the settlement, so that kind's balance is its costs minus the provisions
// that remain.
//
// Every line starts ticked (Lewis, 2026-10-17: a settlement normally
// settles everything for everyone). Whether a chosen cost of its kind
// stands against it (IProvisionCoverage) only decides a warning under it:
// a common line no chosen cost of the building reaches (a key that leaves
// the unit out gives none), a private line or an extra line of the lease
// no chosen cost of its own unit reaches, goes back to the tenant in full.
// A provision added by hand is never warned about, and neither is any line
// of a settlement without a single cost.
//
// Unless no chosen cost charges a tenant anything (Lewis, 2026-10-19):
// owner costs only, or costs whose split gives the tenants nothing. Then
// every line of the leases starts unticked (linesStartTicked), as ticked
// they would only give each tenant their provision back. The broker can
// still tick them. A provision added by hand is ticked when it is added.
//
// The draft keeps what the broker chose:
// - provisionLinesIncluded[contractId][lineKey]: true or false for a line
//   the broker ticked or unticked; absent = as the line starts;
// - provisionsIncluded[contractId] === false: the whole lease is opted out,
//   every line off (drafts made before the lines). Touching one of its
//   lines turns that into a choice per line.
//
// A line's key: the lease's provision line (IProvisionLine.key, or
// `slice:<slice>` for a slice without a line, see provisionLineRows.utils),
// `added:<id>` for a provision the broker added by hand, and WHOLE_LEASE_KEY
// for one amount typed in for the whole lease (a draft made before the
// slices).
//
// The ledger stays per slice and per lease (settledProvisions.utils): a
// slice whose lines are all off counts no month, so a later settlement can
// still settle them; a slice with at least one line on counts its months,
// at the amount of the lines left on (see the engine's
// includedLeaseProvision).

import {
  ADDITIONAL_LINE_KEY_PREFIX,
  COMMON_LINE_KEY,
  NON_COMMON_LINE_KEY,
} from "./contractProvision.utils";
import type { ProvisionSlice } from "./provisionHistory";
import type { SettlementDraftState } from "./types";

// The one row of a lease whose provision was typed in as one amount.
export const WHOLE_LEASE_KEY = "lease";

const ADDED_PREFIX = "added:";
const SLICE_PREFIX = "slice:";

// The key of a provision the broker added by hand.
export const addedProvisionLineKey = (extraId: string): string =>
  `${ADDED_PREFIX}${extraId}`;

export const isAddedProvisionLineKey = (lineKey: string): boolean =>
  lineKey.startsWith(ADDED_PREFIX);

// Which lines of a settlement a chosen cost stands against (the engine's
// ComputeResult.provisionCoverage).
export interface IProvisionCoverage {
  // Per lease: the slices that a chosen cost of the same kind reaches.
  coveredSlicesByContract: Record<string, ProvisionSlice[]>;
  // At least one cost is chosen.
  hasCosts: boolean;
  // A chosen cost gives at least one tenant a share: its split charges a
  // lease something (read before a departure or a forfait moves it).
  chargesTenants: boolean;
}

// Whether a line the broker made no choice for counts: yes, unless costs
// are chosen and none of them charges a tenant anything. A settlement
// without any cost is ticked to say so, and gives every provision back.
export const linesStartTicked = (
  coverage: Pick<IProvisionCoverage, "hasCosts" | "chargesTenants">,
): boolean => !coverage.hasCosts || coverage.chargesTenants;

// The slice a line of the lease counts in: the common costs' line in
// "common", the non-common costs' and every extra line in "private".
// Undefined for one amount typed in for the whole lease, and for a key this
// does not know.
export const sliceOfLineKey = (lineKey: string): ProvisionSlice | undefined => {
  if (lineKey.startsWith(SLICE_PREFIX)) {
    const slice = lineKey.slice(SLICE_PREFIX.length);
    return slice === "common" || slice === "private" ? slice : undefined;
  }
  if (lineKey === COMMON_LINE_KEY) return "common";
  if (
    lineKey === NON_COMMON_LINE_KEY ||
    lineKey.startsWith(ADDITIONAL_LINE_KEY_PREFIX)
  ) {
    return "private";
  }
  return undefined;
};

// Whether a chosen cost stands against a line of the lease: a cost of its
// kind reaches the tenant. A provision added by hand always is, and so is
// every line of a settlement without a single cost (it says so itself).
export const isLineCovered = (params: {
  contractId: string;
  lineKey: string;
  coverage: IProvisionCoverage;
}): boolean => {
  const { contractId, lineKey, coverage } = params;
  if (isAddedProvisionLineKey(lineKey) || !coverage.hasCosts) return true;
  const covered = coverage.coveredSlicesByContract[contractId] ?? [];
  const slice = sliceOfLineKey(lineKey);
  return slice ? covered.includes(slice) : covered.length > 0;
};

// Whether a line gets the warning that no chosen cost of its kind stands
// against it, so its provision goes back to the tenant in full
// (settlement.provisions.uncovered.warning): a ticked line that counts a
// provision with no chosen cost (or correction) of its kind against it. A
// line of € 0 has nothing to give back (M27).
export const showsUncoveredNote = (params: {
  contractId: string;
  lineKey: string;
  coverage: IProvisionCoverage;
  included: boolean;
  amount: number;
}): boolean =>
  params.included && Math.abs(params.amount) >= 0.005 && !isLineCovered(params);

type TInclusionDraft = Pick<
  SettlementDraftState,
  "provisionsIncluded" | "provisionLinesIncluded"
>;

// Whether the whole lease is opted out.
export const isLeaseOptedOut = (
  draft: TInclusionDraft,
  contractId: string,
): boolean => draft.provisionsIncluded?.[contractId] === false;

// The broker's own choice for one line, when they made one.
const chosenFor = (
  draft: TInclusionDraft,
  contractId: string,
  lineKey: string,
): boolean | undefined => draft.provisionLinesIncluded?.[contractId]?.[lineKey];

// Whether one provision line of a lease counts: what the broker chose,
// else how the lease's lines start (linesStartTicked; ticked when not
// given). A provision added by hand always starts ticked.
export const isProvisionLineIncluded = (params: {
  draft: TInclusionDraft;
  contractId: string;
  lineKey: string;
  startsTicked?: boolean;
}): boolean => {
  const { draft, contractId, lineKey, startsTicked = true } = params;
  if (isLeaseOptedOut(draft, contractId)) return false;
  return (
    chosenFor(draft, contractId, lineKey) ??
    (startsTicked || isAddedProvisionLineKey(lineKey))
  );
};

// The draft fields after ticking or unticking some lines of a lease: every
// line in `changes` becomes the broker's choice, the lines chosen before
// keep their choice, and the others stay as they start. `lineKeys`
// holds every line of the lease the step shows: a lease that was opted out
// as a whole gets a choice per line (off for the ones not changed), and a
// line the step no longer shows (a provision added by hand and removed)
// loses its choice. A lease without any line keeps or loses its opt-out by
// `changes[WHOLE_LEASE_KEY]` alone.
export const setProvisionLinesIncluded = (params: {
  draft: TInclusionDraft;
  contractId: string;
  lineKeys: string[];
  changes: Record<string, boolean>;
}): Required<TInclusionDraft> => {
  const { draft, contractId, lineKeys, changes } = params;
  const provisionsIncluded = { ...draft.provisionsIncluded };
  const provisionLinesIncluded = { ...draft.provisionLinesIncluded };
  const wasOptedOut = isLeaseOptedOut(draft, contractId);
  delete provisionsIncluded[contractId];
  delete provisionLinesIncluded[contractId];
  if (lineKeys.length === 0) {
    const isOn = changes[WHOLE_LEASE_KEY] ?? !wasOptedOut;
    if (!isOn) provisionsIncluded[contractId] = false;
    return { provisionsIncluded, provisionLinesIncluded };
  }
  const chosen: Record<string, boolean> = {};
  for (const lineKey of lineKeys) {
    const choice =
      changes[lineKey] ??
      chosenFor(draft, contractId, lineKey) ??
      (wasOptedOut ? false : undefined);
    if (choice != null) chosen[lineKey] = choice;
  }
  if (Object.keys(chosen).length > 0) {
    provisionLinesIncluded[contractId] = chosen;
  }
  return { provisionsIncluded, provisionLinesIncluded };
};

// The keys of every line the step shows for a lease: its provision lines
// (WHOLE_LEASE_KEY for one amount typed in for the whole lease, none when no
// line counts an amount) and the provisions added by hand.
export const leaseLineKeys = (params: {
  // The keys of the lease's line rows; undefined for one amount typed in
  // for the whole lease.
  rowKeys?: string[];
  extraIds: string[];
}): string[] => [
  ...(params.rowKeys ?? [WHOLE_LEASE_KEY]),
  ...params.extraIds.map(addedProvisionLineKey),
];

// Ticks or unticks every line of several leases at once: a card's
// select-all, or a tenant's own checkbox.
export const setLeasesIncluded = (params: {
  draft: TInclusionDraft;
  leases: { contractId: string; lineKeys: string[] }[];
  next: boolean;
}): Required<TInclusionDraft> =>
  params.leases.reduce<Required<TInclusionDraft>>(
    (draft, lease) =>
      setProvisionLinesIncluded({
        draft,
        contractId: lease.contractId,
        lineKeys: lease.lineKeys,
        changes: Object.fromEntries(
          (lease.lineKeys.length > 0 ? lease.lineKeys : [WHOLE_LEASE_KEY]).map(
            lineKey => [lineKey, params.next],
          ),
        ),
      }),
    {
      provisionsIncluded: { ...params.draft.provisionsIncluded },
      provisionLinesIncluded: { ...params.draft.provisionLinesIncluded },
    },
  );

// A tenant's or a card's checkbox: all, some or none of its lines ticked.
export type TLinesSelectState = "all" | "some" | "none";

export const linesSelectState = (ticked: boolean[]): TLinesSelectState => {
  const count = ticked.filter(Boolean).length;
  if (count === 0) return "none";
  return count === ticked.length ? "all" : "some";
};
